// Third Party
import * as React from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import 'react-dates/initialize';
import {
  DayPickerRangeController,
  DayPickerRangeControllerShape,
  FocusedInputShape,
  isInclusivelyAfterDay,
} from 'react-dates';

// Utils
import { useMobileSearchModal } from './mobile-search-modal-context';
import { guestDescription } from '@utils/guest-description';
import { isSSR } from '@utils/predicates';
import { guestCapacity } from '@utils/constants/guest-capacity-thresholds';
import { ListingsSearchType } from '@gql';
import { Geometry } from '@contexts/search-context';
import { Feature } from '@contexts/search-context';
import { routes } from '@routes';
import { extractFirstPhraseBeforeComma } from '@utils/extract-first-phrase-before-comma';

// UI
import { Button } from '@components/button';
import { ButtonIcon } from '@components/button-icon';
import { Icon, IconNames } from '@components/icon';
import { Input } from '@components/form/input';
import { MobileSelectAutocomplete } from './mobile-select-autocomplete';
import { Typography } from '@components/typography';

interface IHandleDatesChange {
  startDate: moment.Moment | null;
  endDate: moment.Moment | null;
}

type Props = {
  placeName?: string;
  placeId?: string;
  checkInDate?: moment.Moment | null;
  checkOutDate?: moment.Moment | null;
  adultCount?: number;
  childCount?: number;
  infantCount?: number;
  petCount?: number;
  activeFilters?: Feature[];
  mapZoom?: number;
  geoSearchBox?: Geometry;
  searchType?: ListingsSearchType;
  onSubmit?: (callbackArgs: {
    placeId: string;
    checkInDate: moment.Moment | null;
    checkOutDate: moment.Moment | null;
    adultCount: number;
    childCount: number;
    infantCount: number;
    petCount: number;
    activeFilters?: Feature[];
  }) => void;
};

// Cast DayPickerRangeController to ComponentType to resolve React JSX type conflicts
// This tells TypeScript to treat it as a valid React component that can accept props
const DayPickerComponent =
  DayPickerRangeController as React.ComponentType<DayPickerRangeControllerShape>;

export const MobileSearchModal: React.FC<Props> = ({
  placeName: _placeName,
  placeId: _placeId,
  checkInDate: _checkInDate = null,
  checkOutDate: _checkOutDate = null,
  adultCount: _adultCount = 1,
  childCount: _childCount = 0,
  infantCount: _infantCount = 0,
  petCount: _petCount = 0,
  activeFilters,
  mapZoom,
  searchType: _searchType,
  onSubmit: _onSubmit,
}) => {
  const { mobileSearchModalActive, deactivateMobileSearchModal } =
    useMobileSearchModal();
  const [checkInDate, setCheckInDate] = React.useState<moment.Moment | null>(
    _checkInDate
  );
  const [checkOutDate, setCheckOutDate] = React.useState<moment.Moment | null>(
    _checkOutDate
  );

  const [adultCount, setAdultCount] = React.useState(_adultCount);
  const [childCount, setChildCount] = React.useState(_childCount);
  const [infantCount, setInfantCount] = React.useState(_infantCount);
  const [petCount, setPetCount] = React.useState(_petCount);

  const [place, setPlace] = React.useState<
    { id: string; name: string } | undefined
  >(_placeName ? { name: _placeName, id: _placeId || '' } : undefined);

  const [searchType, setSearchType] = React.useState(_searchType);

  const [dateRangePickerFocusedInput, setDateRangePickerFocusedInput] =
    React.useState<FocusedInputShape | null>('startDate');

  const [guestCountHumanReadableValue, setGuestCountHumanReadableValue] =
    React.useState('');

  React.useEffect(() => {
    const description = guestDescription(
      adultCount,
      childCount,
      infantCount,
      petCount
    );

    setGuestCountHumanReadableValue(description);
  }, [adultCount, childCount, infantCount, petCount]);

  React.useEffect(() => {
    setSearchType(_searchType);
    if (_searchType == ListingsSearchType.Map) {
      setPlace({ id: '', name: 'Map Search' });
    }
  }, [_searchType]);

  React.useEffect(() => {
    if (mobileSearchModalActive) {
      document.body.classList.add('modalActive');
    } else {
      document.body.classList.remove('modalActive');
    }

    // Cleanup function to remove class when component unmounts
    return () => {
      document.body.classList.remove('modalActive');
    };
  }, [mobileSearchModalActive]);

  const handleDatesChange = ({ startDate, endDate }: IHandleDatesChange) => {
    setCheckInDate && setCheckInDate(startDate);
    setCheckOutDate && setCheckOutDate(endDate);
  };

  const clearSelectedFilters = (): void => {
    setPlace(undefined);
    setAdultCount(1);
    setChildCount(0);
    setInfantCount(0);
    setPetCount(0);
    setDateRangePickerFocusedInput('startDate');
    setCheckInDate && setCheckInDate(null);
    setCheckOutDate && setCheckOutDate(null);
  };

  const [searchInputOpen, setSearchInputOpen] = React.useState<boolean>(true);
  const [dateInputOpen, setDateInputOpen] = React.useState<boolean>(false);
  const [guestInputOpen, setGuestInputOpen] = React.useState<boolean>(false);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setValue: React.Dispatch<React.SetStateAction<number>>,
    maxValue: guestCapacity
  ) => {
    e.preventDefault();
    const re = /^[0-9\b]+$/;
    if (e.target.value.startsWith('0')) {
      e.target.value = e.target.value.slice(1);
    }
    if (e.target.value === '' || re.test(e.target.value)) {
      const inputValue = Number(e.target.value);
      const value = inputValue > maxValue ? maxValue : inputValue;
      setValue(value);
    }
  };

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const queryParams = new URLSearchParams(window.location.search);

    _onSubmit &&
      _onSubmit({
        // United States place id
        placeId: place?.id || 'ChIJCzYy5IS16lQRQrfeQ5K5Oxw',
        checkInDate,
        checkOutDate,
        adultCount,
        childCount,
        infantCount,
        petCount,
        activeFilters,
      });

    !_onSubmit &&
      window.location.assign(
        routes.searchShow({
          placeId: place?.id || undefined,
          zoom: place?.id
            ? undefined
            : queryParams.get('zoom') || mapZoom?.toString() || undefined,
          searchType: place?.id ? ListingsSearchType.Region : searchType,
          centerLat: place?.id
            ? undefined
            : queryParams.get('centerLat') || undefined,
          centerLng: place?.id
            ? undefined
            : queryParams.get('centerLng') || undefined,
          southWestLat: place?.id
            ? undefined
            : queryParams.get('southWestLat') || undefined,
          southWestLng: place?.id
            ? undefined
            : queryParams.get('southWestLng') || undefined,
          northEastLat: place?.id
            ? undefined
            : queryParams.get('northEastLat') || undefined,
          northEastLng: place?.id
            ? undefined
            : queryParams.get('northEastLng') || undefined,
          checkInDate: checkInDate?.format('YYYY-MM-DD'),
          checkOutDate: checkOutDate?.format('YYYY-MM-DD'),
          adultCount: adultCount,
          childCount: childCount,
          infantCount: infantCount,
          petCount: petCount,
          activeFeatures:
            activeFilters && activeFilters.map((f) => f.key).join(','),
        })
      );
  };

  if (!isSSR) {
    return ReactDOM.createPortal(
      <Transition
        show={mobileSearchModalActive}
        as="div"
        className="fixed z-50 inset-0"
        style={{ zIndex: 10000 }}
      >
        <div className="block items-end justify-center inset-0 text-center">
          <TransitionChild
            as="div"
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 transition-opacity">
              <div className="absolute inset-0 bg-black opacity-60"></div>
            </div>
          </TransitionChild>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="inline-block align-middle h-screen"></span>
          &#8203;
          <TransitionChild
            className="absolute inset-0 inline-block w-full text-left bg-gray-xlight shadow-xl transform transition-all align-middle m-0 overflow-hidden"
            as="div"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-headline"
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4"
            enterTo="opacity-100 translate-y-0"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-4"
          >
            <div className="absolute inset-0 flex flex-col h-full">
              <div className="flex flex-row justify-start px-2 py-1 border-b border-gray-light">
                <ButtonIcon
                  variant="text"
                  color="gray"
                  size="md"
                  isCircle={true}
                  icon={IconNames.X}
                  ariaDescribedby="close mobile search form"
                  className="h-10 w-10 justify-center items-center z-100d"
                  onClick={() => {
                    // AUTOFOCUS LOGIC: set only search input to be open when modal is opened regardless of what is currently open
                    deactivateMobileSearchModal();
                    setSearchInputOpen(true);
                    setDateInputOpen(false);
                    setGuestInputOpen(false);
                    // if the modal is closed before new search is executed, reset searchType
                    setSearchType(_searchType);
                    // if search was regional before the modal opened, set place values back to initial region
                    if (
                      _searchType == ListingsSearchType.Region &&
                      _placeName &&
                      _placeId
                    ) {
                      setPlace({
                        id: _placeId,
                        name: _placeName,
                      });
                      // if search was geographical before the modal opened, set place values back to map search
                    } else if (_searchType == ListingsSearchType.Map) {
                      setPlace({
                        id: '',
                        name: 'Map Search',
                      });
                    }
                  }}
                />
              </div>

              <div className="flex-1 overflow-y-auto pt-4">
                <div className="flex flex-col h-full">
                  <form onSubmit={onSubmit} className="space-y-2 mx-4 pb-24">
                    {/* Search Place */}
                    <div className="px-4 p-4 bg-white rounded-2xl shadow-md">
                      <Button
                        variant="text"
                        onClick={() => setSearchInputOpen(!searchInputOpen)}
                        className="flex flex-cols-2 items-center space-x-2 w-full"
                      >
                        <div className="flex flex-col w-1/3 text-black text-left">
                          <Typography variant="subtitle1" component="p">
                            Where
                          </Typography>
                        </div>
                        <div className="flex flex-col w-2/3 text-right truncate">
                          {place?.name
                            ? `${extractFirstPhraseBeforeComma(place?.name)}`
                            : 'Anywhere'}
                        </div>
                      </Button>

                      {searchInputOpen && (
                        <div className="pt-2 overflow-auto">
                          <MobileSelectAutocomplete
                            placeName={place?.name ? place.name : ''}
                            onChange={(_name, option) => {
                              if (option) {
                                setPlace({
                                  name: option.label,
                                  id: option.value,
                                });
                                // AUTOFOCUS LOGIC: if the user enters the search input -> move to date input
                                setSearchInputOpen(false);
                                setDateInputOpen(true);
                              }
                            }}
                            onClear={() => setPlace(undefined)}
                            initialValue={
                              place && place.name && place.id
                                ? {
                                    label: place.name,
                                    value: place.id,
                                  }
                                : null
                            }
                          />
                        </div>
                      )}
                    </div>

                    {/* Search Dates */}
                    <div className="px-4 p-4 bg-white rounded-2xl shadow-md">
                      <Button
                        variant="text"
                        onClick={() => setDateInputOpen(!dateInputOpen)}
                        className="flex flex-cols-2 items-center space-x-2 w-full justify-between"
                      >
                        <div className="flex flex-col text-black text-left">
                          <Typography variant="subtitle1" component="p">
                            When
                          </Typography>
                        </div>
                        <div className="flex flex-col w-full text-right items-end">
                          {checkInDate && checkOutDate
                            ? `${checkInDate.format(
                                'M/DD'
                              )} - ${checkOutDate.format('M/DD')}`
                            : 'Add Dates'}
                        </div>
                      </Button>

                      {dateInputOpen && (
                        <div className="">
                          <div className="hidden">
                            <Input
                              label="Check-in"
                              name="checkInDate"
                              value={checkInDate?.format('MMM DD')}
                              onChange={() => {}}
                              dataId={'@filter/check-in-date'}
                            />
                            <Input
                              label="Check-out"
                              name="checkOutDate"
                              value={checkOutDate?.format('MMM DD')}
                              onChange={() => {}}
                              dataId={'@filter/check-out-date'}
                            />
                          </div>

                          <div className="w-full items-center bg-white">
                            <div className="flex h-full w-full z-50 justify-center bg-white">
                              <DayPickerComponent
                                verticalHeight={4}
                                verticalBorderSpacing={0}
                                enableOutsideDays={false}
                                minDate={null}
                                noBorder={true}
                                minimumNights={1}
                                numberOfMonths={1}
                                initialVisibleMonth={null}
                                focusedInput={dateRangePickerFocusedInput}
                                onDatesChange={handleDatesChange}
                                onFocusChange={(
                                  dateRangePickerFocusedInput: FocusedInputShape | null
                                ) => {
                                  setDateRangePickerFocusedInput(
                                    dateRangePickerFocusedInput || 'startDate'
                                  );
                                }}
                                startDate={checkInDate}
                                endDate={checkOutDate}
                                isOutsideRange={(day: moment.Moment) =>
                                  !isInclusivelyAfterDay(day, moment())
                                }
                                hideKeyboardShortcutsPanel={true}
                              />
                            </div>

                            <div className="flex w-full justify-between pr-2">
                              {!checkInDate && !checkOutDate && (
                                <Button
                                  variant="text"
                                  onClick={() => {
                                    setDateInputOpen(false);
                                    setGuestInputOpen(true);
                                  }}
                                >
                                  Skip
                                </Button>
                              )}
                              {checkInDate && (
                                <Button
                                  variant="text"
                                  onClick={() => {
                                    setCheckInDate && setCheckInDate(null);
                                    setCheckInDate(null);
                                    setCheckOutDate && setCheckOutDate(null);
                                    setCheckOutDate(null);
                                    setDateRangePickerFocusedInput('startDate');
                                  }}
                                  dataId={'@filter/clear-dates'}
                                >
                                  Clear
                                </Button>
                              )}
                              <Button
                                onClick={() => {
                                  setDateInputOpen(false);
                                  setGuestInputOpen(true);
                                }}
                                dataId={'@filter/next'}
                                color="primary"
                                variant="outlined"
                              >
                                Next
                              </Button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Search Guests */}
                    <div className="px-4 p-4 bg-white rounded-2xl shadow-md">
                      <Button
                        variant="text"
                        onClick={() => setGuestInputOpen(!guestInputOpen)}
                        className="flex flex-cols-2 items-center space-x-2 w-full justify-between"
                      >
                        <div className="flex flex-col text-black text-left">
                          <Typography variant="subtitle1" component="p">
                            Who
                          </Typography>
                        </div>
                        <div className="flex flex-col w-2/3 text-right items-end">
                          <div className="w-full truncate">
                            {guestCountHumanReadableValue
                              ? `${guestCountHumanReadableValue}`
                              : 'Add Guests'}
                          </div>
                        </div>
                      </Button>

                      {guestInputOpen && (
                        <div className="flex h-full w-full justify-center mx-auto bg-white">
                          <div className="w-full p-4 space-y-4 divide-y divide-gray-light">
                            {/* Adults */}
                            <div className="flex justify-between">
                              <div className="flex flex-col text-left">
                                <Typography
                                  variant="subtitle2"
                                  component="p"
                                  className="pb-2"
                                >
                                  Adults
                                </Typography>
                                <Typography variant="body2" component="p">
                                  Ages 13 or above
                                </Typography>
                              </div>
                              <div className="flex space-x-2 justify-between">
                                <ButtonIcon
                                  icon={IconNames.MINUS}
                                  ariaDescribedby="remove adult"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setAdultCount((val) => {
                                      if (val > 1) {
                                        return val - 1;
                                      } else {
                                        return val;
                                      }
                                    })
                                  }
                                  dataId={'@filter/remove-adult-count'}
                                />
                                <input
                                  type="number"
                                  className="w-6 text-center"
                                  value={adultCount}
                                  onChange={(e) => {
                                    handleValueChange(
                                      e,
                                      setAdultCount,
                                      guestCapacity.ADULTS
                                    );
                                  }}
                                />
                                <ButtonIcon
                                  icon={IconNames.PLUS}
                                  ariaDescribedby="add adult"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setAdultCount((val) => {
                                      return val >= guestCapacity.ADULTS
                                        ? guestCapacity.ADULTS
                                        : val + 1;
                                    })
                                  }
                                  dataId={'@filter/add-adult-count'}
                                />
                              </div>
                            </div>

                            {/* Children */}
                            <div className="flex justify-between pt-4">
                              <div className="flex flex-col">
                                <Typography
                                  variant="subtitle2"
                                  component="p"
                                  className="pb-2"
                                >
                                  Children
                                </Typography>
                                <Typography variant="body2" component="p">
                                  Ages 2-12
                                </Typography>
                              </div>
                              <div className="flex space-x-2 justify-between">
                                <ButtonIcon
                                  icon={IconNames.MINUS}
                                  ariaDescribedby="remove child count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setChildCount((val) => {
                                      if (val > 0) {
                                        return val - 1;
                                      } else {
                                        return val;
                                      }
                                    })
                                  }
                                  dataId={'@filter/remove-child-count'}
                                />
                                <input
                                  type="number"
                                  className="w-6 text-center"
                                  value={childCount}
                                  onChange={(e) => {
                                    handleValueChange(
                                      e,
                                      setChildCount,
                                      guestCapacity.CHILDREN
                                    );
                                  }}
                                />
                                <ButtonIcon
                                  icon={IconNames.PLUS}
                                  ariaDescribedby="add child count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setChildCount((val) => {
                                      return val >= guestCapacity.CHILDREN
                                        ? guestCapacity.CHILDREN
                                        : val + 1;
                                    })
                                  }
                                  dataId={'@filter/add-child-count'}
                                />
                              </div>
                            </div>

                            {/* Infants */}
                            <div className="flex justify-between pt-4">
                              <div className="flex flex-col">
                                <Typography
                                  variant="subtitle2"
                                  component="p"
                                  className="pb-2"
                                >
                                  Infants
                                </Typography>
                                <Typography variant="body2" component="p">
                                  Under 2
                                </Typography>
                              </div>
                              <div className="flex space-x-2 justify-between">
                                <ButtonIcon
                                  icon={IconNames.MINUS}
                                  ariaDescribedby="remove infant count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setInfantCount((val) => {
                                      if (val > 0) {
                                        return val - 1;
                                      } else {
                                        return val;
                                      }
                                    })
                                  }
                                  dataId={'@filter/remove-infant-count'}
                                />
                                <input
                                  type="number"
                                  className="w-6 text-center"
                                  value={infantCount}
                                  onChange={(e) => {
                                    handleValueChange(
                                      e,
                                      setInfantCount,
                                      guestCapacity.INFANTS
                                    );
                                  }}
                                />
                                <ButtonIcon
                                  icon={IconNames.PLUS}
                                  ariaDescribedby="add infant count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setInfantCount((val) => {
                                      return val >= guestCapacity.INFANTS
                                        ? guestCapacity.INFANTS
                                        : val + 1;
                                    })
                                  }
                                  dataId={'@filter/add-infant-count'}
                                />
                              </div>
                            </div>

                            {/* Pets */}
                            <div className="flex justify-between pt-4">
                              <div className="flex flex-col">
                                <Typography
                                  variant="subtitle2"
                                  component="p"
                                  className="pb-2"
                                >
                                  Pets
                                </Typography>
                                <Typography variant="body2" component="p">
                                  Only properties that allow pets will be shown
                                </Typography>
                              </div>
                              <div className="flex space-x-2 justify-between">
                                <ButtonIcon
                                  icon={IconNames.MINUS}
                                  ariaDescribedby="remove pet count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setPetCount((val) => {
                                      if (val > 0) {
                                        return val - 1;
                                      } else {
                                        return val;
                                      }
                                    })
                                  }
                                  dataId={'@filter/remove-pet-count'}
                                />
                                <input
                                  type="number"
                                  className="w-6 text-center"
                                  value={petCount}
                                  onChange={(e) => {
                                    handleValueChange(
                                      e,
                                      setPetCount,
                                      guestCapacity.PETS
                                    );
                                  }}
                                />
                                <ButtonIcon
                                  icon={IconNames.PLUS}
                                  ariaDescribedby="add pet count"
                                  variant="text"
                                  className="text-gray-main"
                                  onClick={() =>
                                    setPetCount((val) => {
                                      return val >= guestCapacity.PETS
                                        ? guestCapacity.PETS
                                        : val + 1;
                                    })
                                  }
                                  dataId={'@filter/add-pet-count'}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Block to account for scrolling if all blocks are expanded */}
                    <div className="h-36 block w-full"></div>

                    {/* Clear / Search */}
                    <div className="-ml-4 block absolute w-full -bottom-0 bg-white pb-3 border-t -mx-4 sm:px-8 md:hidden border border-gray-light shadow-2xl px-4 pt-4 z-40">
                      <div className="flex justify-between items-center">
                        <div className="-ml-1 flex overflow-x-auto w-full justify-center items-center text-center">
                          <>
                            <div className="w-full text-left text-black">
                              <Button
                                onClick={clearSelectedFilters}
                                variant="text"
                                dataId={'@filter/clear-all-filters'}
                              >
                                <Typography
                                  variant="subtitle2"
                                  className="text-black"
                                >
                                  Clear all filters
                                </Typography>
                              </Button>
                            </div>
                            <div className="text-right  w-full items-end">
                              <Button
                                type="submit"
                                className="ml-2"
                                onClick={() => {
                                  setSearchInputOpen(false);
                                  setDateInputOpen(false);
                                  setGuestInputOpen(false);
                                }}
                                dataId={'@filter/search-properties'}
                              >
                                <Icon
                                  name={IconNames.MAGNIFYING_GLASS}
                                  solid={true}
                                  className="pr-2"
                                />
                                Search
                              </Button>
                            </div>
                          </>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Footer - Clear/Search buttons */}
              <div className="sticky bottom-0 bg-white pb-3 border-t border-gray-light shadow-2xl px-4 pt-4">
                <div className="flex justify-between items-center">
                  <div className="-ml-1 flex overflow-x-auto w-full justify-center items-center text-center">
                    <>
                      <div className="w-full text-left text-black">
                        <Button
                          onClick={() => clearSelectedFilters()}
                          variant="text"
                          dataId={'@filter/clear-all-filters'}
                        >
                          <Typography
                            variant="subtitle2"
                            className="text-black"
                          >
                            Clear all filters
                          </Typography>
                        </Button>
                      </div>
                      <div className="text-right  w-full items-end">
                        <Button
                          type="submit"
                          className="ml-2"
                          onClick={() => {
                            setSearchInputOpen(false);
                            setDateInputOpen(false);
                            setGuestInputOpen(false);
                          }}
                          dataId={'@filter/search-properties'}
                        >
                          <Icon
                            name={IconNames.MAGNIFYING_GLASS}
                            solid={true}
                            className="text-white pr-2"
                          />
                          Search
                        </Button>
                      </div>
                    </>
                  </div>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </Transition>,
      document.getElementById('modal-root') as Element
    );
  } else {
    return null;
  }
};
